import {
  type SurveyAnswers,
  type SurveyAnswerValue,
  type SurveySubmitAnswers,
} from '@factorialco/f0-react'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'

import { QuestionType, QuestionnaireType } from 'modules/forms/components/newFormEditor/types'
import { Element } from 'modules/forms/lib/Element'
import { Form } from 'modules/forms/lib/Form'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'
import { Answer, ElementOption } from 'modules/forms/lib/types'

function isSectionElement(el: Element | SectionElement): el is SectionElement {
  return 'elements' in el && Array.isArray(el.elements)
}

export const cloneElementOptions = (
  options: ElementOption[] | null | undefined
): ElementOption[] => {
  if (!options) {
    return []
  }

  return options.map((option) => ({
    id: option.id,
    value: option.value,
    label: option.label,
    isCorrect: option.isCorrect,
  }))
}

function mapFormElementTypeToQuestionType(
  type: Element['getType'] extends () => infer T ? T : never
): QuestionType['type'] {
  switch (type) {
    case 'text':
      return 'text'
    case 'longtext':
      return 'longtext'
    case 'select':
      return 'select'
    case 'multiselect':
      return 'multiselect'
    case 'dropdownsingle':
      return 'dropdownsingle'
    case 'dropdownmulti':
      return 'dropdownmulti'
    case 'number':
      return 'number'
    case 'rating':
      return 'rating'
    case 'score':
      return 'score'
    case 'url':
      return 'url'
    case 'date':
      return 'date'
    case 'checkbox':
      return 'checkbox'
    default:
      return 'text'
  }
}

type SanitizedParams = QuestionType['params']

function sanitizeParams(params: Element['params']): SanitizedParams {
  const sanitized: SanitizedParams = {}

  Object.entries(params).forEach(([key, value]) => {
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value === null
    ) {
      sanitized[key] = value
    }
  })

  return sanitized
}

function mapOptionsForQuestion(options: ElementOption[]): QuestionType['options'] {
  const mapped = cloneElementOptions(options)
  return mapped.length > 0 ? mapped : null
}

function mapQuestion(element: Element, sectionId?: string): QuestionType {
  return {
    id: element.id,
    sectionId,
    label: element.label,
    description: element.description,
    type: mapFormElementTypeToQuestionType(element.getType()),
    position: element.position,
    mandatory: element.mandatory,
    locked: element.isLocked(),
    params: sanitizeParams(element.params),
    options: mapOptionsForQuestion(element.options),
  }
}

function sortElementsByPosition<T extends { position: number; id: string }>(elements: T[]): T[] {
  return [...elements].sort((a, b) => {
    if (a.position !== b.position) {
      return a.position - b.position
    }

    return a.id.localeCompare(b.id, undefined, { numeric: true })
  })
}

export function mapFormToQuestionnaire(form: Form): QuestionnaireType {
  const elements: QuestionnaireType['elements'] = sortElementsByPosition(form.elements).map(
    (element) => {
      if (isSectionElement(element)) {
        return {
          id: element.id,
          label: element.label,
          description: element.description,
          type: 'section',
          position: element.position,
          mandatory: element.mandatory,
          locked: element.isLocked(),
          params: sanitizeParams(element.params),
          options: mapOptionsForQuestion(element.options),
          elements: sortElementsByPosition(element.elements).map((child) =>
            mapQuestion(child, element.id)
          ),
        }
      }

      return mapQuestion(element)
    }
  )

  return {
    title: form.title,
    description: form.description ?? '',
    elements,
  }
}

function mapSubmittedAnswer(element: Element, value: SurveySubmitAnswers[string]): Answer {
  switch (element.getType()) {
    case 'select':
    case 'dropdownsingle': {
      if (typeof value !== 'string') {
        return { answer: '' }
      }

      const selectedOption = element.options.find(
        (option) => option.value === value || option.id === value
      )
      const selectedOptionId = selectedOption?.id

      return {
        answer: selectedOptionId ?? value,
        options: selectedOptionId ? [selectedOptionId] : undefined,
      }
    }

    case 'multiselect':
    case 'dropdownmulti': {
      if (!Array.isArray(value)) {
        return { answer: '' }
      }

      const selectedOptionIds = value
        .map((selectedValue) => {
          const selectedOption = element.options.find(
            (option) => option.value === selectedValue || option.id === selectedValue
          )

          return selectedOption?.id
        })
        .filter((optionId): optionId is string => optionId !== undefined)

      return {
        answer: selectedOptionIds.length > 0 ? selectedOptionIds.join(',') : value.join(','),
        options: selectedOptionIds.length > 0 ? selectedOptionIds : undefined,
      }
    }

    case 'rating':
    case 'score': {
      if (typeof value !== 'number') {
        return { answer: '' }
      }

      const selectedOption = element.options.find((option) => Number(option.value) === value)
      const selectedOptionId = selectedOption?.id

      return {
        answer: String(value),
        options: selectedOptionId ? [selectedOptionId] : undefined,
      }
    }

    case 'number': {
      if (typeof value !== 'number') {
        return { answer: '' }
      }

      return { answer: String(value) }
    }

    case 'date': {
      if (value instanceof Date) {
        return { answer: i18nDateToString(value, 'backend') ?? '' }
      }

      if (typeof value === 'string') {
        return { answer: value }
      }

      return { answer: '' }
    }

    case 'checkbox': {
      if (typeof value === 'boolean') {
        return { answer: value ? 'true' : '' }
      }
      return { answer: '' }
    }

    default: {
      if (typeof value === 'string') {
        return { answer: value }
      }

      return { answer: '' }
    }
  }
}

export function applySurveySubmitAnswersToForm(form: Form, answers: SurveySubmitAnswers): void {
  Object.entries(answers).forEach(([elementId, value]) => {
    const element = form.getElement(elementId)

    if (!element) {
      return
    }

    element.answer = mapSubmittedAnswer(element, value)
  })
}

function mapElementAnswerToSurveyAnswerValue(element: Element): SurveyAnswerValue | undefined {
  const answerValue = element.answer?.answer
  if (answerValue === undefined || answerValue === null || answerValue === '') return undefined

  switch (element.getType()) {
    case 'text':
    case 'longtext':
      return { type: element.getType() === 'longtext' ? 'longText' : 'text', value: answerValue }
    case 'rating':
    case 'score':
      return { type: 'rating', value: answerValue ? Number(answerValue) : null }
    case 'number':
      return { type: 'numeric', value: answerValue ? Number(answerValue) : null }
    case 'select':
    case 'dropdownsingle': {
      const option = element.options?.find((o) => o.id === answerValue || o.value === answerValue)
      return {
        type: element.getType() === 'dropdownsingle' ? 'dropdown-single' : 'select',
        value: option?.id || option?.value || answerValue,
      }
    }
    case 'multiselect':
    case 'dropdownmulti': {
      const ids = answerValue.split(',').filter(Boolean)
      if (ids.length === 0) return undefined
      const values = ids.map((id) => {
        const option = element.options?.find((o) => o.id === id || o.value === id)
        return option?.id || option?.value || answerValue
      })
      return {
        type: element.getType() === 'dropdownmulti' ? 'dropdown-multi' : 'multi-select',
        value: values,
      }
    }
    case 'url':
      return { type: 'link', value: answerValue }
    case 'checkbox':
      return { type: 'checkbox', value: answerValue === 'true' }
    case 'date':
      return {
        type: 'date',
        value: answerValue ? stringToDate(answerValue, 'backend').toDate() : null,
      }
    default:
      return { type: 'text', value: answerValue }
  }
}

export function mapFormAnswersToSurveyAnswers(form: Form): Partial<SurveyAnswers> {
  const answers: Partial<SurveyAnswers> = {}

  for (const element of form.elements) {
    if (isSectionElement(element)) {
      for (const child of element.elements) {
        const value = mapElementAnswerToSurveyAnswerValue(child)
        if (value) answers[child.id] = value
      }
    } else {
      const value = mapElementAnswerToSurveyAnswerValue(element)
      if (value) answers[element.id] = value
    }
  }

  return answers
}
